import * as React from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';
import { Surface, type SurfaceProps } from './Card';
import { Inline, Stack } from './Layout';
import { KeyValueRow } from './List';
import type { BadgeProps, JobPhase } from './Badge';
import { StatusPillMenu, type StatusMenu } from './StatusPillMenu';
import { Text } from './Text';

export interface DriverInfoCardViewProps
  extends Omit<SurfaceProps, 'children'> {
  name: string;
  /** The status code — ACTIVE, DISPATCHED, … — which picks the badge colour. */
  status: string;
  /** The status as words; the code is shown when omitted. */
  statusLabel?: string;
  /**
   * What colours the pill, so it matches the driver's pin and row: the job
   * phase for a driver on a call, or a tone (green for Available, neutral
   * for "No signal"). With neither the pill is plain.
   */
  statusPhase?: JobPhase;
  statusTone?: BadgeProps['tone'];
  /** The truck's number, or empty when none is assigned. */
  truck?: string;
  phone?: string | null;
  email?: string;
  onClose: () => void;
  /**
   * A menu for the status pill. Left out for a user who may not edit it,
   * and the pill is a plain badge.
   */
  statusMenu?: StatusMenu;
}

/**
 * The panel that opens over the map when a driver's marker is clicked.
 *
 *   <DriverInfoCardView name="Marcus Webb" status="ACTIVE" statusLabel="Active"
 *     statusTone="success" truck="T-14" phone="+1 555 0100" email="marcus@…"
 *     onClose={close} className="absolute top-4 right-4" />
 *
 * Where it floats is the map's business, so it takes a className for that
 * and nothing else. The map says what colour the status is, the same as
 * it says for the driver's pin and row, so the three never disagree.
 */
export const DriverInfoCardView = React.forwardRef<
  HTMLDivElement,
  DriverInfoCardViewProps
>(function DriverInfoCardView(
  {
    name,
    status,
    statusLabel,
    statusPhase,
    statusTone,
    truck,
    phone,
    email,
    onClose,
    statusMenu,
    ...props
  },
  ref
) {
  return (
    <Surface
      ref={ref}
      bordered
      radius="lg"
      padding="md"
      elevation="overlay"
      {...props}
    >
      <Stack gap={3}>
        <Inline gap={0} align="start" justify="between">
          <Text variant="h4" tone="default">
            {name}
          </Text>
          <Button
            size="icon-xs"
            variant="subtle"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="size-icon-sm" />
          </Button>
        </Inline>

        <Stack gap={0}>
          <KeyValueRow
            label="Status"
            density="compact"
            value={
              <StatusPillMenu
                value={status}
                label={statusLabel ?? status}
                phase={statusPhase}
                tone={statusTone}
                options={statusMenu?.options ?? []}
                onChange={statusMenu?.onChange}
                busy={statusMenu?.busy ?? false}
                align="end"
              />
            }
          />
          <KeyValueRow
            label="Truck"
            density="compact"
            value={truck || 'No truck assigned'}
          />
          <KeyValueRow label="Phone" density="compact" value={phone} />
          <KeyValueRow
            label="Email"
            density="compact"
            value={
              <Text
                as="span"
                variant="inherit"
                truncate
                className="block max-w-[150px]"
              >
                {email}
              </Text>
            }
          />
        </Stack>
      </Stack>
    </Surface>
  );
});
